import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Send as SendIcon,
  // Article as TemplateIcon, // 削除
  Close as CloseIcon,
  Verified as VerifiedIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useMessageReply } from '../../hooks/useMessageReply';
import {
  ReplyType,
  ReplyMessageRequest,
  ThreadMessage,
} from '../../types/message-reply';
import { formatRelativeTime } from '../../utils/dateUtils';
import { MainLayout } from '../../components/Layout/MainLayout';

const MessageDetailPage: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const {
    currentThread,
    messages,
    // templates は削除
    loading,
    replyLoading,
    loadThreadDetail,
    sendReply,
    // processTemplate は削除
  } = useMessageReply();

  const [replyText, setReplyText] = useState('');
  const [replyType, setReplyType] = useState<ReplyType>(ReplyType.CUSTOM);
  // テンプレート機能は削除されました

  useEffect(() => {
    if (threadId) {
      loadThreadDetail(threadId);
    }
  }, [threadId, loadThreadDetail]);

  const handleBack = () => {
    navigate('/messages');
  };

  // テンプレート機能は削除されました

  const handleSendReply = async () => {
    if (!threadId || !replyText.trim()) return;

    try {
      const replyData: ReplyMessageRequest = {
        message_body: replyText,
        // reply_type は削除（バックエンドで自動的に 'custom' に設定）
      };

      await sendReply(threadId, replyData);
      setReplyText('');
      setReplyType(ReplyType.CUSTOM);
    } catch (error) {
      console.error('Failed to send reply:', error);
    }
  };

  const getReplyTypeLabel = (type: ReplyType) => {
    // 返信タイプは常にカスタムのみ
    return 'カスタム返信';
  };

  if (loading) {
    return (
      <MainLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Typography>読み込み中...</Typography>
        </Container>
      </MainLayout>
    );
  }

  if (!currentThread) {
    return (
      <MainLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Typography>メッセージが見つかりません</Typography>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* ヘッダー */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
            メッセージ詳細
          </Typography>
        </Box>

        {/* スレッド情報 */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar>
                <BusinessIcon />
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">
                    {currentThread.company?.name || '企業名不明'}
                  </Typography>
                  {currentThread.company?.verified && (
                    <VerifiedIcon color="primary" fontSize="small" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {currentThread.company?.business_type || '業種不明'}
                </Typography>
              </Box>
            </Box>
            <Typography variant="h5" gutterBottom>
              {currentThread.subject}
            </Typography>
            {currentThread.tags && currentThread.tags.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {currentThread.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* メッセージ履歴 */}
        <Box sx={{ mb: 4 }}>
          {messages &&
            messages.map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                isLast={index === messages.length - 1}
              />
            ))}
        </Box>

        {/* 返信フォーム */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              返信を作成
            </Typography>

            {/* 返信タイプ選択とテンプレート機能は削除されました */}

            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="返信メッセージを入力してください..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 1000 }}
              error={
                !!(replyText && replyText.length > 0 && replyText.length < 5)
              }
              helperText={
                replyText && replyText.length > 0 && replyText.length < 5
                  ? '5文字以上入力してください'
                  : `${replyText?.length || 0}/1000文字`
              }
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSendReply}
                disabled={
                  !replyText?.trim() ||
                  (replyText?.length || 0) < 5 ||
                  replyLoading
                }
              >
                {replyLoading ? '送信中...' : '返信送信'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* テンプレート選択ダイアログ */}
        {/* テンプレート選択ダイアログは削除されました */}
      </Container>
    </MainLayout>
  );
};

interface MessageBubbleProps {
  message: ThreadMessage;
  isLast: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isLast }) => {
  // 企業からのメッセージは左側、学生からのメッセージは右側に表示
  const isCompany = message.sender_type === 'company';

  return (
    <Box sx={{ mb: isLast ? 0 : 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: isCompany ? 'flex-start' : 'flex-end',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        {isCompany && (
          <Avatar sx={{ bgcolor: 'primary.light' }}>
            <BusinessIcon />
          </Avatar>
        )}

        <Paper
          sx={{
            maxWidth: '70%',
            p: 2,
            bgcolor: isCompany ? 'grey.100' : 'primary.main',
            color: isCompany ? 'text.primary' : 'white',
          }}
        >
          {/* 送信者情報 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {message.sender?.name || '送信者不明'}
            </Typography>
            {message.sender?.title && (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                ({message.sender.title})
              </Typography>
            )}
          </Box>

          {/* メッセージ本文 */}
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              mb: 1,
            }}
          >
            {message.message_body}
          </Typography>

          {/* 返信タイプ（学生の返信の場合） */}
          {!isCompany && message.reply_type && (
            <Chip
              label={getReplyTypeLabel(message.reply_type)}
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'inherit',
                mb: 1,
              }}
            />
          )}

          {/* タイムスタンプ */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              <AccessTimeIcon
                fontSize="small"
                sx={{ mr: 0.5, verticalAlign: 'middle' }}
              />
              {formatRelativeTime(message.sent_at)}
            </Typography>
            {message.is_ai_assisted && (
              <Chip
                label="AI支援"
                size="small"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'inherit',
                  fontSize: '0.7rem',
                  height: 20,
                }}
              />
            )}
          </Box>

          {message.read_at && (
            <Typography
              variant="caption"
              sx={{ opacity: 0.6, display: 'block', mt: 0.5 }}
            >
              既読: {formatRelativeTime(message.read_at)}
            </Typography>
          )}
        </Paper>

        {!isCompany && (
          <Avatar sx={{ bgcolor: 'secondary.light' }}>
            <PersonIcon />
          </Avatar>
        )}
      </Box>
    </Box>
  );
};

const getReplyTypeLabel = (type: ReplyType) => {
  // 返信タイプは常にカスタムのみ
  return 'カスタム返信';
};

export default MessageDetailPage;
